import type { StatusTone } from "../../components/ui";
import type tunnels from "../../locales/en/tunnels.json";
import type { Tunnel, TunnelConnectivity } from "../../lib/types";

export interface TunnelStatus {
  tone: StatusTone;
  /**
   * The state's name under `tunnels.state`, not the words themselves.
   *
   * This module is shared by the list and the detail page and is not a
   * component, so it cannot reach a translator — and a hook here would make a
   * plain function into one. Naming the copy keeps the decision (which signal
   * wins) here and the words where every other string on these pages lives.
   */
  key: keyof typeof tunnels.state;
}

/**
 * What the tunnel is doing, once the engine has told us.
 *
 * `unhealthy` is the state this whole signal exists for: the container is up,
 * the listeners accept connections, and every one of them hangs — which used to
 * be indistinguishable from working.
 */
const LINK_COPY: Record<Exclude<TunnelConnectivity, "unknown">, TunnelStatus> = {
  healthy: { tone: "good", key: "healthy" },
  starting: { tone: "warn", key: "starting" },
  unhealthy: { tone: "bad", key: "unhealthy" },
};

/**
 * What the container is doing, in words rather than the column's own value.
 *
 * This is what the list has to work from: connectivity costs an engine round
 * trip per tunnel and is read on the detail page only, so a row says what the
 * database knows and the page behind it says what WireGuard knows.
 */
const STATE_COPY: Record<Tunnel["state"], TunnelStatus> = {
  pending: { tone: "warn", key: "pending" },
  // Deliberately not "connected": the container being up is not the tunnel
  // carrying traffic, and only the detail page asks the second question.
  running: { tone: "good", key: "running" },
  stopped: { tone: "neutral", key: "stopped" },
  /*
   * Covers both ways a tunnel ends up here: a create the engine refused, which
   * never reached a container, and one that reached it and would not start.
   * The customer's next move is the same for both and the row is billed for
   * neither, so the wording says that rather than guessing which happened.
   */
  error: { tone: "bad", key: "error" },
};

/**
 * The one status line, from whichever signal is actually available.
 *
 * Connectivity when the engine could answer, the container's own state when it
 * could not. The two say different things and only one of them is about
 * traffic: a tunnel whose endpoint has gone away keeps running, so "Running"
 * must never stand in for "working" once something better is available.
 */
export function tunnelStatus(
  state: Tunnel["state"],
  connectivity: TunnelConnectivity = "unknown",
): TunnelStatus {
  /*
   * A failed create outranks any connectivity reading. There is no container to
   * ask, so the engine's answer is "starting" forever — which reads as a tunnel
   * coming up rather than one that never will.
   */
  if (state === "error" || connectivity === "unknown") return STATE_COPY[state];
  return LINK_COPY[connectivity];
}
